import React, { useEffect, useState, useMemo } from 'react'
import styled from '@emotion/styled'
import { OverrideType, GlobalScheduleDefaults, ScheduleOverride } from './types'
import { useScheduleState } from './hooks/useScheduleState'
import { useCalendarResolver } from './hooks/useCalendarResolver'
import { useHolidays, CountryCode } from './hooks/useHolidays'
import ScheduleConfigForm from './ScheduleConfigForm'
import OverridesPanel from './OverridesPanel'
import YearlyCalendarView from './YearlyCalendarView'
import UniversalTimesPanel from './UniversalTimesPanel'
import { ScheduleSettings } from '@types'
import { getDefaultScheduleGlobals } from '@utils/groupOrderUtils'

interface Props {
  clientSettingId: string
  scheduleSettings: ScheduleSettings | null
  displayFailureMessage: (message: string) => void
  clientCountry?: CountryCode
  onUpdateScheduleSettings?: (settings: ScheduleSettings) => void
  onSaveUniversalSettings?: () => Promise<void>
  newScheduleSettings?: () => ScheduleSettings
}

const GOClientScheduleManager: React.FC<Props> = ({
  clientSettingId,
  scheduleSettings,
  displayFailureMessage,
  clientCountry = 'CA',
  onUpdateScheduleSettings,
  onSaveUniversalSettings,
  newScheduleSettings,
}) => {
  const {
    schedules,
    selectedScheduleId,
    rules,
    overrides,
    allRulesBySchedule,
    allOverridesBySchedule,
    isLoading,
    isDirty,
    overlapError,
    selectSchedule,
    createSchedule,
    deleteSchedule,
    updateSchedule,
    addRule,
    updateRule,
    removeRule,
    addOverride,
    removeOverride,
    saveSchedule,
    discardChanges,
    loadSchedule,
  } = useScheduleState()

  const [year, setYear] = useState(() => new Date().getFullYear())
  const holidays = useHolidays(year, clientCountry)
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  const [pendingOverride, setPendingOverride] = useState<{
    date: string
    type: OverrideType
    existing?: ScheduleOverride
  } | null>(null)

  const globalDefaults = useMemo(
    () => getDefaultScheduleGlobals(scheduleSettings) as GlobalScheduleDefaults,
    [scheduleSettings],
  )

  const resolvedDays = useCalendarResolver(
    year,
    globalDefaults,
    schedules,
    allRulesBySchedule,
    allOverridesBySchedule,
  )

  useEffect(() => {
    loadSchedule(clientSettingId)
  }, [clientSettingId, loadSchedule])

  const handleSave = async () => {
    try {
      await saveSchedule()
    } catch {
      displayFailureMessage('Failed to save schedule. Please try again.')
    }
  }

  const handleDayClick = (date: string) => {
    const day = resolvedDays.get(date)
    if (!day) {
      return
    }

    // If the day already has an ad hoc override, open the modal to edit it
    if (day.override) {
      setPendingOverride({
        date,
        type: day.override.overrideType,
        existing: day.override,
      })

      return
    }

    const type =
      day.status === 'service' || day.status === 'ad-hoc-on'
        ? OverrideType.Cancel
        : OverrideType.Add

    setPendingOverride({ date, type })
  }

  if (isLoading) {
    return <LoadingText>Loading schedule...</LoadingText>
  }

  return (
    <Layout>
      <LeftPanel collapsed={leftCollapsed}>
        <CollapseToggle
          onClick={() => setLeftCollapsed((c) => !c)}
          title={leftCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {leftCollapsed ? '›' : '‹'}
        </CollapseToggle>

        {!leftCollapsed && (
          <PanelContent>
            {onUpdateScheduleSettings &&
              onSaveUniversalSettings &&
              newScheduleSettings && (
                <>
                  <UniversalTimesPanel
                    scheduleSettings={scheduleSettings}
                    onUpdate={onUpdateScheduleSettings}
                    onSave={onSaveUniversalSettings}
                    onCreate={newScheduleSettings}
                  />
                  <Spacer />
                </>
              )}

            <ScheduleConfigForm
              schedules={schedules}
              selectedScheduleId={selectedScheduleId}
              rules={rules}
              isDirty={isDirty}
              overlapError={overlapError}
              onSelectSchedule={selectSchedule}
              onCreateSchedule={createSchedule}
              onDeleteSchedule={deleteSchedule}
              onUpdateSchedule={updateSchedule}
              onAddRule={addRule}
              onUpdateRule={updateRule}
              onRemoveRule={removeRule}
              onSave={handleSave}
              onDiscard={discardChanges}
            />

            <Spacer />

            <OverridesPanel
              overrides={overrides}
              onAddOverride={addOverride}
              onRemoveOverride={removeOverride}
              pendingOverride={pendingOverride}
              onClearPendingOverride={() => setPendingOverride(null)}
            />
          </PanelContent>
        )}
      </LeftPanel>

      <RightPanel>
        <YearlyCalendarView
          year={year}
          resolvedDays={resolvedDays}
          holidays={holidays}
          onYearChange={setYear}
          onDayClick={handleDayClick}
        />
      </RightPanel>
    </Layout>
  )
}

export default GOClientScheduleManager

// ── Styled Components ──

const Layout = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  min-width: 0;
  align-items: flex-start;
`

const LeftPanel = styled.div<{ collapsed: boolean }>`
  flex: 0 0 ${({ collapsed }) => (collapsed ? '28px' : '680px')};
  transition: flex-basis 0.2s ease;
  overflow: hidden;
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
`

const CollapseToggle = styled.button`
  flex: 0 0 auto;
  align-self: flex-end;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  color: #757575;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;

  &:hover {
    background: #eeeeee;
    color: #424242;
  }
`

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 6px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 2px;
  }
`

const RightPanel = styled.div`
  flex: 1 1 0;
  min-width: 320px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  overflow-x: auto;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 3px;
  }
`

const Spacer = styled.div`
  height: 10px;
`

const LoadingText = styled.p`
  color: #757575;
  font-size: 14px;
  padding: 20px;
`
